import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { connectLabToClinicTemplate } from 'apollo/templates/organization/mutation.graphql';
import { clinicConnectedLabsTemplate, AllLabsForClinicTemplate } from 'apollo/templates/organization/query.graphql';
import React, { createElement, FC, createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type ConnectedLab = {
    _id: string;
    logoSignedUrl?: string;
    isDisabled: boolean;
    categoryNames: string[];
    ratePercent?: number;
    totalRating?: number;
    lab: any;
};

type LabsContextType = {
    connectedLabs: ConnectedLab[];
    allLabs: any[];
    isLoading: boolean;
    isLoadingAllLabs: boolean;
    isSorting: boolean;
    isSearching: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    error: string | null;
    sortOrder: 'asc' | 'desc';
    searchTerm: string;
    statusFilter: 'all' | 'active' | 'inactive';
    allLabsSortOrder: 'asc' | 'desc';
    allLabsSearchText: string;
    setSortOrder: (order: 'asc' | 'desc') => Promise<void>;
    setSearchTerm: (term: string) => Promise<void>;
    setStatusFilter: (status: 'all' | 'active' | 'inactive') => Promise<void>;
    setAllLabsSortOrder: (order: 'asc' | 'desc') => void;
    setAllLabsSearchText: (text: string) => void;
    loadMore: () => Promise<void>;
    refetch: () => void;
    fetchAllLabs: (searchText?: string, sortOrder?: string) => void;
    connectToLab: (labId: string) => Promise<void>;
    isConnectingLab: (labId: string) => boolean;
    isAnyLabConnecting: boolean;
};

const LabsContext = createContext<LabsContextType | undefined>(undefined);

export const LabsProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [allLabsSortOrder, setAllLabsSortOrder] = useState<'asc' | 'desc'>('asc');
    const [allLabsSearchText, setAllLabsSearchText] = useState<string>('');
    const [isSorting, setIsSorting] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [connectedLabs, setConnectedLabs] = useState<ConnectedLab[]>([]);
    const [allLabs, setAllLabs] = useState<any[]>([]);

    const [connectingLabIds, setConnectingLabIds] = useState<Set<string>>(new Set());
    const limit = 20;

    // Escape regex special characters
    const escapeRegex = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Shared Query Variables - memoized to prevent stale closures
    const getConnectedVariables = useCallback(
        (override?: any) => ({
            sortOrder,
            searchText: searchTerm ? escapeRegex(searchTerm) : undefined,
            status: statusFilter === 'all' ? undefined : statusFilter,
            limit,
            offset: 0,
            ...override,
        }),
        [sortOrder, searchTerm, statusFilter],
    );

    const getAllLabsVariables = (override?: any) => ({
        onlyPublishedLabs: true,
        searchText: allLabsSearchText ? escapeRegex(allLabsSearchText) : undefined,
        sortBy: 'name',
        sortOrder: allLabsSortOrder,
        ...override,
    });

    // Fetch All Labs
    const [getAllLabsForClinic, { loading: isLoadingAllLabs }] = useLazyQuery(AllLabsForClinicTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const allLabsData = data?.allLabsForClinic || [];
            setAllLabs(allLabsData);
        },
    });

    // Connected Labs Query - exclude searchText to prevent double queries
    const { loading, error, fetchMore, data } = useQuery(clinicConnectedLabsTemplate, {
        variables: getConnectedVariables({ searchText: undefined }),
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            const result = data?.clinicConnectedLabs;
            if (result) {
                setConnectedLabs(result.connectedLabs || []);
            }
        },
    });

    // Load More
    const [hasMore, setHasMore] = useState(true);

    const loadMore = async () => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        try {
            await fetchMore({
                variables: getConnectedVariables({ offset: connectedLabs.length }),
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;

                    const newLabs = fetchMoreResult.clinicConnectedLabs.connectedLabs;
                    const merged = [...prev.clinicConnectedLabs.connectedLabs, ...newLabs];

                    setConnectedLabs(merged);
                    setHasMore(newLabs.length === limit);

                    return {
                        clinicConnectedLabs: {
                            ...prev.clinicConnectedLabs,
                            connectedLabs: merged,
                        },
                    };
                },
            });
        } finally {
            setIsLoadingMore(false);
        }
    };

    // Centralized Refetch Function
    const refetchConnectedLabs = useCallback(
        async (extraVars?: any) => {
            return fetchMore({
                variables: getConnectedVariables(extraVars),
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;

                    const labs = fetchMoreResult.clinicConnectedLabs.connectedLabs;
                    setConnectedLabs(labs);
                    setHasMore(labs.length === limit);

                    return fetchMoreResult;
                },
            });
        },
        [fetchMore, getConnectedVariables],
    );

    // Handlers (Sort / Search / Filter)
    const handleSetSortOrder = async (order: 'asc' | 'desc') => {
        setSortOrder(order);
        setIsSorting(true);
        try {
            await refetchConnectedLabs({ sortOrder: order });
        } finally {
            setIsSorting(false);
        }
    };

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const AllLabsDebounceRef = useRef<NodeJS.Timeout | null>(null);

    const handleSetSearchTerm = useCallback(
        async (term: string) => {
            setSearchTerm(term);
            if (debounceRef.current) clearTimeout(debounceRef.current);

            debounceRef.current = setTimeout(async () => {
                setIsSearching(true);
                try {
                    await fetchMore({
                        variables: {
                            sortOrder,
                            searchText: term ? escapeRegex(term) : undefined,
                            status: statusFilter === 'all' ? undefined : statusFilter,
                            limit,
                            offset: 0,
                        },
                        updateQuery: (prev, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return prev;

                            const labs = fetchMoreResult.clinicConnectedLabs.connectedLabs;
                            setConnectedLabs(labs);
                            setHasMore(labs.length === limit);

                            return fetchMoreResult;
                        },
                    });
                } finally {
                    setIsSearching(false);
                }
            }, 300);
        },
        [fetchMore, sortOrder, statusFilter],
    );

    const handleSetAllLabsSearchText = useCallback(
        (text: string) => {
            setAllLabsSearchText(text);
            if (AllLabsDebounceRef.current) clearTimeout(AllLabsDebounceRef.current);

            AllLabsDebounceRef.current = setTimeout(() => {
                const variables = getAllLabsVariables({ searchText: text ? escapeRegex(text) : undefined });
                getAllLabsForClinic({ variables });
            }, 300);
        },
        [getAllLabsForClinic, getAllLabsVariables],
    );

    const handleSetStatusFilter = async (status: 'all' | 'active' | 'inactive') => {
        setStatusFilter(status);
        setIsSearching(true);
        try {
            await refetchConnectedLabs({ status: status === 'all' ? undefined : status });
        } finally {
            setIsSearching(false);
        }
    };

    // Fetch All Labs
    const fetchAllLabs = useCallback(
        async (searchText?: string) => {
            if (searchText !== undefined) {
                setAllLabsSearchText(searchText);
            }
            const variables = getAllLabsVariables({ searchText });
            await getAllLabsForClinic({ variables });
        },
        [getAllLabsForClinic, getAllLabsVariables],
    );

    const handleSetAllLabsSortOrder = useCallback(
        (order: 'asc' | 'desc') => {
            setAllLabsSortOrder(order);
            const variables = getAllLabsVariables({ sortOrder: order });
            getAllLabsForClinic({ variables });
        },
        [getAllLabsForClinic, getAllLabsVariables],
    );

    const { showBoundary } = useDenthubErrorBoundary();

    // Connect Lab Mutation
    const [connectLabMutation] = useMutation(connectLabToClinicTemplate, {
        // DD-3626: e.g. the lab was deactivated meanwhile; shown as a feedback dialog
        onError: showBoundary,
        onCompleted: () => {
            const variables = getAllLabsVariables();
            getAllLabsForClinic({ variables });
            refetchConnectedLabs();
        },
    });

    const connectToLab = async (labId: string) => {
        setConnectingLabIds((prev) => new Set(prev).add(labId));
        try {
            await connectLabMutation({ variables: { lab: labId } });
        } finally {
            setConnectingLabIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(labId);
                return newSet;
            });
        }
    };

    const isConnectingLab = useCallback((labId: string) => connectingLabIds.has(labId), [connectingLabIds]);
    const isAnyLabConnecting = connectingLabIds.size > 0;

    // Provider Value
    const value: LabsContextType = {
        connectedLabs,
        allLabs,
        isLoading: loading,
        isLoadingAllLabs,
        isSorting,
        isSearching,
        isLoadingMore,
        hasMore,
        error: error?.message || null,
        sortOrder,
        searchTerm,
        statusFilter,
        allLabsSortOrder,
        allLabsSearchText,
        setSortOrder: handleSetSortOrder,
        setSearchTerm: handleSetSearchTerm,
        setStatusFilter: handleSetStatusFilter,
        setAllLabsSortOrder: handleSetAllLabsSortOrder,
        setAllLabsSearchText: handleSetAllLabsSearchText,
        loadMore,
        refetch: () => refetchConnectedLabs(),
        fetchAllLabs,
        connectToLab,
        isConnectingLab,
        isAnyLabConnecting,
    };

    return createElement(LabsContext.Provider, { value }, children);
};

export const useLabs = () => {
    const context = useContext(LabsContext);
    if (!context) throw new Error('useLabs must be used within a LabsProvider');
    return context;
};
